import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCreateWalkIn } from '@/api/hooks/useAdminBookings';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import { useAdminSports } from '@/api/hooks/useAdminSports';
import { pickLocaleValue } from '@/lib/i18n-pick';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';

const PAYMENT_METHODS = ['CASH', 'COMP', 'TERMINAL'] as const;

const schema = z.object({
  customerEmail: z.email(),
  customerPhone: z.string().optional(),
  customerFirstName: z.string().optional(),
  customerLastName: z.string().optional(),
  locationId: z.string().min(1),
  sportCode: z.string().min(1),
  date: z.string().min(1),
  time: z.string().min(1),
  duration: z.coerce.number().min(30).max(180),
  playerCount: z.coerce.number().min(1).max(8),
  paymentMethod: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function WalkInDialog({ open, onOpenChange }: Props) {
  const { t, i18n } = useTranslation();
  const createWalkIn = useCreateWalkIn();

  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];
  const defaultLocationId = locations[0]?.locationId ?? '';

  // Tenant-active sports (replaces the old local 3-sport Pickering roster).
  // useAdminSports calls GET /api/v1/admin/sports which is already scoped to
  // the operator's tenant via AdminSecurityService.getOperatorTenantId.
  const { data: tenantSports } = useAdminSports();
  const sports = tenantSports ?? [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as never,
    defaultValues: {
      locationId: defaultLocationId,
      sportCode: 'GOLF_SIM',
      date: new Date().toISOString().split('T')[0],
      time: '',
      duration: 60,
      playerCount: 1,
      paymentMethod: 'CASH',
      customerEmail: '',
      customerPhone: '',
      customerFirstName: '',
      customerLastName: '',
    },
  });

  const locationId = watch('locationId');
  const sportCode = watch('sportCode');
  const paymentMethod = watch('paymentMethod');

  const locationLabel =
    locations.find((l) => l.locationId === locationId)?.locationName ??
    t('admin.all_locations');

  const matchedSport = sports.find((s) => s.code === sportCode);
  const sportLabel = matchedSport
    ? t(`sport_${matchedSport.code}`, { defaultValue: pickLocaleValue(matchedSport.name, i18n.language) })
    : sportCode;

  const onSubmit = async (data: FormValues) => {
    const startTime = new Date(`${data.date}T${data.time}:00`).toISOString();

    try {
      await createWalkIn.mutateAsync({
        locationId: data.locationId,
        sportCode: data.sportCode,
        startTime,
        duration: data.duration,
        playerCount: data.playerCount,
        paymentMethod: data.paymentMethod,
        customerEmail: data.customerEmail,
        customerPhone: data.customerPhone || undefined,
        customerFirstName: data.customerFirstName || undefined,
        customerLastName: data.customerLastName || undefined,
      });
      toast.success(t('admin.walk_in_success'));
      reset();
      onOpenChange(false);
    } catch (err: unknown) {
      const appErr = err as { code?: string };
      if (appErr.code === 'SLOT_UNAVAILABLE') {
        toast.error(t('admin.walkin_slot_unavailable'));
      } else {
        toast.error(t('common.error'));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('admin.walk_in_title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Customer info */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>{t('admin.walk_in_customer_email')}</Label>
              <Input
                type="email"
                {...register('customerEmail')}
                className="mt-1"
              />
              {errors.customerEmail && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.customerEmail.message}
                </p>
              )}
            </div>
            <div>
              <Label>{t('admin.walk_in_first_name')}</Label>
              <Input {...register('customerFirstName')} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.walk_in_last_name')}</Label>
              <Input {...register('customerLastName')} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('admin.walk_in_customer_phone')}</Label>
              <Input {...register('customerPhone')} className="mt-1" />
            </div>
          </div>

          {/* Booking details */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t('admin.walk_in_sport')}</Label>
              <Select
                value={sportCode}
                onValueChange={(v) => setValue('sportCode', v ?? '')}
              >
                <SelectTrigger className="mt-1">
                  <span className="text-sm">{sportLabel}</span>
                </SelectTrigger>
                <SelectContent>
                  {sports.map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {t(`sport_${s.code}`, { defaultValue: pickLocaleValue(s.name, i18n.language) })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin.walk_in_payment')}</Label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setValue('paymentMethod', v ?? '')}
              >
                <SelectTrigger className="mt-1">
                  <span className="text-sm">{paymentMethod}</span>
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {locations.length > 1 && (
              <div className="sm:col-span-2">
                <Label>Location</Label>
                <Select
                  value={locationId}
                  onValueChange={(v) => setValue('locationId', v ?? '')}
                >
                  <SelectTrigger className="mt-1">
                    <span className="text-sm">{locationLabel}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.locationId} value={loc.locationId}>
                        {loc.locationName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t('admin.walk_in_date')}</Label>
              <Input type="date" {...register('date')} className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.walk_in_time')}</Label>
              <Input type="time" {...register('time')} className="mt-1" />
              {errors.time && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.time.message}
                </p>
              )}
            </div>
            <div>
              <Label>{t('admin.walk_in_duration')}</Label>
              <Input
                type="number"
                step={30}
                min={30}
                max={180}
                {...register('duration')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('admin.walk_in_players')}</Label>
              <Input
                type="number"
                min={1}
                max={8}
                {...register('playerCount')}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('admin.walk_in_creating')
                : t('admin.walk_in_create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
